import React, { useState, useRef } from 'react';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';

const LIP_SYNC_STYLES = [
  { id: 'realistic', label: 'Realistic Talking', icon: 'User', color: '#6366F1', desc: 'Natural human-like lip movement' },
  { id: 'cartoon', label: 'Cartoon Talking', icon: 'Smile', color: '#F59E0B', desc: 'Animated cartoon character style' },
  { id: 'podcast', label: 'Podcast Presenter', icon: 'Mic', color: '#10B981', desc: 'Professional presenter look' },
  { id: 'cinematic', label: 'Cinematic Character', icon: 'Film', color: '#EC4899', desc: 'Dramatic cinematic quality' },
];

const GENERATION_STEPS = [
  'Analyzing facial features...',
  'Mapping lip geometry...',
  'Processing audio waveform...',
  'Synchronizing lip movement...',
  'Generating facial animation...',
  'Adding head motion...',
  'Rendering final video...',
];

export default function LipSyncWorkspace({ onGenerated, disabled }) {
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const [scriptText, setScriptText] = useState('');
  const [inputMode, setInputMode] = useState('audio'); // 'audio' | 'script'
  const [selectedStyle, setSelectedStyle] = useState('realistic');
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [stepIndex, setStepIndex] = useState(0);
  const [result, setResult] = useState(null);
  const [imageSafetyStatus, setImageSafetyStatus] = useState(null);
  const imageInputRef = useRef(null);
  const audioInputRef = useRef(null);
  const progressRef = useRef(null);
  const stepRef = useRef(null);

  const handleImageUpload = (file) => {
    if (!file) return;
    setImageSafetyStatus('checking');
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e?.target?.result);
      // Simulate safety check
      setTimeout(() => {
        setImageSafetyStatus('approved');
        setUploadedImage(file);
      }, 1500);
    };
    reader?.readAsDataURL(file);
  };

  const handleGenerate = () => {
    if ((!uploadedImage && !imagePreview) || isGenerating) return;
    if (inputMode === 'audio' && !audioFile) return;
    if (inputMode === 'script' && !scriptText?.trim()) return;

    setIsGenerating(true);
    setProgress(0);
    setStepIndex(0);
    setResult(null);

    let current = 0;
    progressRef.current = setInterval(() => {
      current += 1.1;
      if (current >= 90) { current = 90; clearInterval(progressRef?.current); }
      setProgress(current);
    }, 130);

    stepRef.current = setInterval(() => {
      setStepIndex((prev) => (prev < GENERATION_STEPS?.length - 1 ? prev + 1 : prev));
    }, 1600);

    setTimeout(() => {
      clearInterval(progressRef?.current);
      clearInterval(stepRef?.current);
      setProgress(100);
      const style = LIP_SYNC_STYLES?.find((s) => s?.id === selectedStyle);
      const generated = {
        id: Date.now(),
        title: `${style?.label} - Lip Sync Video`,
        style: selectedStyle,
        duration: inputMode === 'script' ? `${Math.ceil(scriptText?.split(' ')?.length / 2.5)}s` : '0:15',
        thumbnail: imagePreview,
        mode: 'lip-sync',
        createdAt: new Date()?.toLocaleDateString('en-US'),
      };
      setResult(generated);
      setIsGenerating(false);
      onGenerated?.(generated);
    }, 12000);
  };

  const canGenerate = imagePreview && imageSafetyStatus === 'approved' &&
    (inputMode === 'script' ? scriptText?.trim() : audioFile);

  return (
    <div className="flex flex-col gap-4">
      {/* Style selector */}
      <div>
        <p className="text-sm font-semibold mb-2" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Animation Style</p>
        <div className="grid grid-cols-2 gap-2">
          {LIP_SYNC_STYLES?.map((style) => {
            const active = selectedStyle === style?.id;
            return (
              <button
                key={style?.id}
                onClick={() => !disabled && !isGenerating && setSelectedStyle(style?.id)}
                disabled={disabled || isGenerating}
                className="flex items-center gap-2 p-2.5 rounded-xl border text-left transition-all duration-200 disabled:opacity-50"
                style={{
                  borderColor: active ? style?.color : 'var(--color-border)',
                  background: active ? `${style?.color}12` : 'var(--color-card)',
                  boxShadow: active ? `0 0 0 2px ${style?.color}30` : 'none',
                }}
              >
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: active ? style?.color : `${style?.color}20` }}>
                  <Icon name={style?.icon} size={15} color={active ? '#fff' : style?.color} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold" style={{ color: active ? style?.color : 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>{style?.label}</p>
                  <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)', fontSize: 9 }}>{style?.desc}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
      {/* Image upload */}
      <div>
        <p className="text-sm font-semibold mb-2" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Upload Character Image</p>
        <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e?.target?.files?.[0])} />
        {imagePreview ? (
          <div className="relative rounded-xl overflow-hidden" style={{ height: 140 }}>
            <img src={imagePreview} alt="Character preview" className="w-full h-full object-cover" />
            <div className="absolute inset-0 flex items-end p-2" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 60%)' }}>
              <div className="flex items-center gap-2 w-full">
                {imageSafetyStatus === 'checking' && (
                  <div className="flex items-center gap-1.5 px-2 py-1 rounded-full" style={{ background: 'rgba(245,158,11,0.9)' }}>
                    <Icon name="Loader" size={10} color="white" />
                    <span className="text-xs font-semibold text-white" style={{ fontFamily: 'var(--font-caption)' }}>Checking safety...</span>
                  </div>
                )}
                {imageSafetyStatus === 'approved' && (
                  <div className="flex items-center gap-1.5 px-2 py-1 rounded-full" style={{ background: 'rgba(16,185,129,0.9)' }}>
                    <Icon name="CheckCircle2" size={10} color="white" />
                    <span className="text-xs font-semibold text-white" style={{ fontFamily: 'var(--font-caption)' }}>Image approved</span>
                  </div>
                )}
                <button
                  onClick={() => { setImagePreview(null); setUploadedImage(null); setImageSafetyStatus(null); }}
                  className="ml-auto w-6 h-6 rounded-full flex items-center justify-center"
                  style={{ background: 'rgba(239,68,68,0.9)' }}
                >
                  <Icon name="X" size={11} color="white" />
                </button>
              </div>
            </div>
          </div>
        ) : (
          <button
            onClick={() => imageInputRef?.current?.click()}
            disabled={disabled || isGenerating}
            className="w-full flex flex-col items-center gap-2 py-6 rounded-xl border-2 border-dashed transition-all hover:opacity-80 disabled:opacity-50"
            style={{ borderColor: 'var(--color-border)', background: 'var(--color-muted)' }}
          >
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(99,102,241,0.12)' }}>
              <Icon name="ImagePlus" size={20} color="var(--color-primary)" />
            </div>
            <div className="text-center">
              <p className="text-xs font-semibold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Upload Character Image</p>
              <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>JPG, PNG, WebP supported</p>
            </div>
          </button>
        )}
      </div>
      {/* Audio / Script toggle */}
      <div>
        <div className="flex items-center gap-1 p-1 rounded-xl mb-3" style={{ background: 'var(--color-muted)' }}>
          {[{ id: 'audio', label: 'Upload Audio', icon: 'Upload' }, { id: 'script', label: 'Type Script', icon: 'Type' }]?.map((opt) => (
            <button
              key={opt?.id}
              onClick={() => !isGenerating && setInputMode(opt?.id)}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all"
              style={{
                background: inputMode === opt?.id ? 'var(--color-card)' : 'transparent',
                color: inputMode === opt?.id ? 'var(--color-foreground)' : 'var(--color-muted-foreground)',
                boxShadow: inputMode === opt?.id ? 'var(--shadow-sm)' : 'none',
                fontFamily: 'var(--font-caption)',
              }}
            >
              <Icon name={opt?.icon} size={12} />
              {opt?.label}
            </button>
          ))}
        </div>

        {inputMode === 'audio' ? (
          <div>
            <input ref={audioInputRef} type="file" accept=".mp3,.wav,.aac,.m4a" className="hidden" onChange={(e) => setAudioFile(e?.target?.files?.[0])} />
            {audioFile ? (
              <div className="flex items-center gap-3 p-3 rounded-xl border" style={{ background: 'rgba(99,102,241,0.06)', borderColor: 'rgba(99,102,241,0.3)' }}>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'var(--color-primary)' }}>
                  <Icon name="Music" size={16} color="white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold truncate" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>{audioFile?.name}</p>
                  <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>{(audioFile?.size / 1024)?.toFixed(0)} KB</p>
                </div>
                <button onClick={() => setAudioFile(null)} className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: 'var(--color-muted)' }}>
                  <Icon name="X" size={11} color="var(--color-muted-foreground)" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => audioInputRef?.current?.click()}
                disabled={disabled || isGenerating}
                className="w-full flex flex-col items-center gap-2 py-5 rounded-xl border-2 border-dashed transition-all hover:opacity-80 disabled:opacity-50"
                style={{ borderColor: 'var(--color-border)', background: 'var(--color-muted)' }}
              >
                <Icon name="Upload" size={18} color="var(--color-primary)" />
                <div className="text-center">
                  <p className="text-xs font-semibold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Upload Audio File</p>
                  <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>MP3, WAV, AAC, M4A</p>
                </div>
              </button>
            )}
          </div>
        ) : (
          <textarea
            value={scriptText}
            onChange={(e) => setScriptText(e?.target?.value)}
            placeholder="Type the script for your character to speak..."
            rows={4}
            disabled={disabled || isGenerating}
            className="w-full resize-none rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] text-[var(--color-foreground)] placeholder-[var(--color-muted-foreground)] p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] transition-all"
            style={{ fontFamily: 'var(--font-body)' }}
          />
        )}
      </div>
      {/* Generate button */}
      <button
        onClick={handleGenerate}
        disabled={!canGenerate || isGenerating || disabled}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ background: 'linear-gradient(135deg, #EC4899, #F43F5E)', color: 'white', fontFamily: 'var(--font-heading)' }}
      >
        <Icon name={isGenerating ? 'Loader' : 'Mic'} size={16} color="white" />
        {isGenerating ? 'Generating Lip Sync Video...' : 'Generate Lip Sync Video'}
      </button>
      {/* Generation progress */}
      {isGenerating && (
        <div className="rounded-2xl p-4 flex flex-col gap-3 animate-fade-in-up" style={{ background: 'rgba(236,72,153,0.06)', border: '1px solid rgba(236,72,153,0.2)' }}>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center pulse-glow" style={{ background: '#EC4899' }}>
              <Icon name="Mic" size={14} color="white" />
            </div>
            <div>
              <p className="text-xs font-semibold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Generating lip sync video...</p>
              <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>{GENERATION_STEPS?.[stepIndex]}</p>
            </div>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--color-muted)' }}>
            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #EC4899, #F43F5E)' }} />
          </div>
          <p className="text-xs text-right" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-data)' }}>{Math.round(progress)}%</p>
        </div>
      )}
      {/* Result */}
      {result && (
        <div className="rounded-2xl p-4 animate-fade-in-up" style={{ background: 'rgba(236,72,153,0.06)', border: '1px solid rgba(236,72,153,0.3)' }}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0">
              {result?.thumbnail ? (
                <img src={result?.thumbnail} alt="Generated character" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center" style={{ background: '#EC4899' }}>
                  <Icon name="Mic" size={18} color="white" />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold truncate" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>{result?.title}</p>
              <p className="text-xs" style={{ color: '#EC4899', fontFamily: 'var(--font-caption)' }}>Lip Sync · {result?.duration}</p>
            </div>
            <div className="flex items-center gap-1 px-2 py-1 rounded-full" style={{ background: 'rgba(16,185,129,0.12)' }}>
              <Icon name="CheckCircle2" size={11} color="var(--color-success)" />
              <span className="text-xs font-semibold" style={{ color: 'var(--color-success)', fontFamily: 'var(--font-caption)' }}>Ready</span>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {[{ icon: 'Play', label: 'Preview' }, { icon: 'Download', label: 'Download' }, { icon: 'Share2', label: 'Share' }]?.map((btn) => (
              <button
                key={btn?.label}
                className="flex flex-col items-center gap-1 py-2 rounded-xl border text-xs font-medium transition-all hover:opacity-80"
                style={{ borderColor: 'var(--color-border)', background: 'var(--color-muted)', color: 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}
              >
                <Icon name={btn?.icon} size={13} color="var(--color-muted-foreground)" />
                {btn?.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
